import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { PaymentServiceWithVersionsResponseType } from '../../../../api/types/payments/payment-template';
import { getPaymentServicesListOperation } from '../operations/get-payment-services-list-operation';
import {
  PaymentServicesFilterType,
  defaultPaymentServicesFilter,
} from '../payment-services-filter.type';
import { PaymentServicesFilter } from './payment-services-filter';

interface PaymentServicesTableProps {
  // Selection mode. When set, the table renders a selection column and the
  // parent owns the selection state. Selection persists across pages because
  // the DataTable is keyed by `id`.
  // - 'multiple' → checkbox column; use `selectedServices` + `onSelectionChange`.
  // - 'single'   → radio column;     use `selectedService`  + `onSingleSelectionChange`.
  selectionMode?: 'single' | 'multiple';

  // For selectionMode === 'multiple'
  selectedServices?: PaymentServiceWithVersionsResponseType[];
  onSelectionChange?: (
    services: PaymentServiceWithVersionsResponseType[],
  ) => void;

  // For selectionMode === 'single'
  selectedService?: PaymentServiceWithVersionsResponseType | null;
  onSingleSelectionChange?: (
    service: PaymentServiceWithVersionsResponseType | null,
  ) => void;

  // Per-row actions (used by the payments admin page).
  onEditRow?: (service: PaymentServiceWithVersionsResponseType) => void;
  onDeleteRow?: (service: PaymentServiceWithVersionsResponseType) => void;
  deleteLoadingForId?: number | null;

  // External refresh trigger: parent increments this to force a refetch of the
  // current page (e.g. after a create / edit / delete completed elsewhere).
  refreshKey?: number;
}

export const PaymentServicesTable: React.FC<PaymentServicesTableProps> = ({
  selectionMode,
  selectedServices,
  onSelectionChange,
  selectedService,
  onSingleSelectionChange,
  onEditRow,
  onDeleteRow,
  deleteLoadingForId,
  refreshKey,
}) => {
  const [data, setData] = useState<PaymentServiceWithVersionsResponseType[]>(
    [],
  );
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);

  // `filters` is the live model bound to the filter inputs.
  // `appliedFilters` is the snapshot used by pagination/refresh, so editing
  // inputs without pressing "חפש" doesn't change the result set on screen.
  const [filters, setFilters] = useState<PaymentServicesFilterType>(
    defaultPaymentServicesFilter(),
  );
  const [appliedFilters, setAppliedFilters] =
    useState<PaymentServicesFilterType>(filters);

  const fetchPage = useCallback(
    async (
      nextFilters: PaymentServicesFilterType,
      nextOffset: number,
      nextLimit: number,
    ) => {
      await getPaymentServicesListOperation(
        nextFilters,
        nextOffset,
        nextLimit,
        setData,
        setTotal,
        setLoading,
      );
    },
    [],
  );

  // Mount + external refresh trigger. The closure reads the latest
  // appliedFilters/offset/limit (state from the same render), which is exactly
  // what we want for "refresh the page currently on screen".
  useEffect(() => {
    fetchPage(appliedFilters, offset, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleSearch = async () => {
    setAppliedFilters(filters);
    setOffset(0);
    await fetchPage(filters, 0, limit);
  };

  const handleClear = () => {
    setFilters(defaultPaymentServicesFilter());
  };

  const onPageChange = async (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    await fetchPage(appliedFilters, newOffset, newLimit);
  };

  const showActionsColumn = !!onEditRow || !!onDeleteRow;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <PaymentServicesFilter
        filters={filters}
        loading={loading}
        onChange={setFilters}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      <div
        style={{
          fontSize: '14px',
          color: '#4b5563',
          textAlign: 'right',
        }}
      >
        סה״כ רשומות: {total.toLocaleString()}
      </div>

      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        value={data}
        loading={loading}
        lazy
        paginator
        rows={limit}
        first={offset}
        totalRecords={total}
        onPage={onPageChange}
        rowsPerPageOptions={[10, 20, 50, 100]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        dataKey="id"
        selectionMode={selectionMode}
        selection={
          selectionMode === 'single'
            ? (selectedService ?? undefined)
            : selectionMode === 'multiple'
              ? (selectedServices ?? [])
              : undefined
        }
        onSelectionChange={
          selectionMode === 'single'
            ? (e) =>
                onSingleSelectionChange?.(
                  (e.value as PaymentServiceWithVersionsResponseType | null) ??
                    null,
                )
            : selectionMode === 'multiple'
              ? (e) =>
                  onSelectionChange?.(
                    e.value as PaymentServiceWithVersionsResponseType[],
                  )
              : undefined
        }
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
      >
        {selectionMode && (
          <Column
            selectionMode={selectionMode}
            headerStyle={{ width: '3rem' }}
          />
        )}
        <Column
          style={{ textAlign: 'right' }}
          field="paymentServiceName"
          header="שם שרות"
        />

        <Column
          style={{ textAlign: 'right' }}
          field="paymentServiceTypeName"
          header="סוג שרות"
        />
        <Column
          body={(rowData: PaymentServiceWithVersionsResponseType) => (
            <div>
              ₪
              {Number(rowData.versions[0]?.price ?? 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          )}
          header="מחיר"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="createdByName"
          header="נוצר ע״י"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="createdAt"
          header="תאריך יצירה"
          style={{ textAlign: 'right' }}
          body={(rowData: PaymentServiceWithVersionsResponseType) =>
            rowData.createdAt
              ? new Date(rowData.createdAt).toLocaleDateString('he-IL')
              : ''
          }
        />
        {showActionsColumn && (
          <Column
            style={{ textAlign: 'right' }}
            field="פעולות"
            header=""
            body={(rowData: PaymentServiceWithVersionsResponseType) => (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {onEditRow && (
                  <Button
                    icon="pi pi-pencil"
                    text
                    severity="warning"
                    tooltip={'עריכת שרות תשלום'}
                    onClick={() => onEditRow(rowData)}
                  />
                )}
                {onDeleteRow && (
                  <Button
                    icon="pi pi-trash"
                    text
                    severity="danger"
                    tooltip={'מחיקת שרות תשלום'}
                    loading={deleteLoadingForId === rowData.id}
                    disabled={
                      deleteLoadingForId !== null &&
                      deleteLoadingForId !== undefined
                    }
                    onClick={() => onDeleteRow(rowData)}
                  />
                )}
              </div>
            )}
          />
        )}
      </DataTable>
    </div>
  );
};
